import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  createMetadataItem,
  deleteMetadataItem,
  fetchEvaluationScore,
  fetchMetadataItems,
  updateMetadataItem
} from '../api';
import { getTexts } from '../utils/configLoader';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import { Skeleton } from './ui/skeleton';
import { ClipboardList, Plus } from 'lucide-react';

const EmployeeEvaluation = () => {
  const texts = getTexts('evaluation');
  const { toast } = useToast();
  const CHECKLIST_TYPE_KEY = 'rules';
  const [callId, setCallId] = useState('');
  const [evaluation, setEvaluation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [checklistItems, setChecklistItems] = useState([]);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistError, setChecklistError] = useState(null);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({ name: '', description: '', instructions: '' });

  const handleLoadEvaluation = async () => {
    if (!callId) {
      setError('Укажите идентификатор звонка.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchEvaluationScore(callId);
      setEvaluation(result);
    } catch (err) {
      console.error(err);
      setError('Не удалось загрузить оценку. Проверьте идентификатор звонка.');
      setEvaluation(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadChecklistItems();
  }, []);

  const loadChecklistItems = async () => {
    setChecklistLoading(true);
    setChecklistError(null);
    try {
      const data = await fetchMetadataItems(CHECKLIST_TYPE_KEY);
      setChecklistItems(data);
    } catch {
      setChecklistError('Не удалось загрузить чек-лист. Повторите попытку позже.');
    } finally {
      setChecklistLoading(false);
    }
  };

  const handleChecklistDialogChange = (open) => {
    setItemDialogOpen(open);
    if (!open) {
      setEditingItem(null);
      setItemForm({ name: '', description: '', instructions: '' });
    }
  };

  const handleOpenItemDialog = (item = null) => {
    setEditingItem(item);
    if (item) {
      setItemForm({
        name: item.name ?? '',
        description: item.description ?? '',
        instructions: item.instructions ?? ''
      });
    } else {
      setItemForm({ name: '', description: '', instructions: '' });
    }
    setItemDialogOpen(true);
  };

  const handleSaveItem = async () => {
    if (!itemForm.name.trim()) {
      return;
    }

    const payload = {
      name: itemForm.name.trim(),
      description: itemForm.description ?? '',
      instructions: itemForm.instructions ?? ''
    };

    try {
      if (editingItem) {
        await updateMetadataItem(CHECKLIST_TYPE_KEY, editingItem.id, payload);
        toast({ title: 'Критерий обновлен' });
      } else {
        await createMetadataItem(CHECKLIST_TYPE_KEY, payload);
        toast({ title: 'Критерий создан' });
      }
      handleChecklistDialogChange(false);
      await loadChecklistItems();
    } catch {
      toast({ title: 'Не удалось сохранить критерий', variant: 'destructive' });
    }
  };

  const handleDeleteItem = async (item) => {
    if (!item) {
      return;
    }

    if (!window.confirm(`Удалить критерий "${item.name}"?`)) {
      return;
    }

    try {
      await deleteMetadataItem(CHECKLIST_TYPE_KEY, item.id);
      toast({ title: 'Критерий удален' });
      await loadChecklistItems();
    } catch {
      toast({ title: 'Не удалось удалить критерий', variant: 'destructive' });
    }
  };

  const totalScore = evaluation?.total ?? 0;
  const percentage = evaluation?.percentage ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">{texts.title}</h2>
          <p className="text-muted-foreground">{texts.description}</p>
        </div>
        <Button onClick={() => handleOpenItemDialog()} className="self-start">
          <Plus className="mr-2 h-4 w-4" />
          {texts.addCriterion}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Чек-лист оценки качества
          </CardTitle>
          <CardDescription>Критерии для оценки качества звонков сотрудников</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {checklistError && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              {checklistError}
            </div>
          )}
          <div className="border rounded-lg overflow-hidden">
            <div className="relative w-full overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12 text-center">{texts.table.number}</TableHead>
                    <TableHead>{texts.table.rule}</TableHead>
                    <TableHead>Инструкции</TableHead>
                    <TableHead className="w-48 text-right">{texts.table.actions}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {checklistLoading && (
                    <>
                      {Array.from({ length: 4 }).map((_, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Skeleton className="h-4 w-6" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-4 w-48" />
                            <div className="mt-2">
                              <Skeleton className="h-4 w-32" />
                            </div>
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-4 w-full" />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="ml-auto flex w-full justify-end gap-2">
                              <Skeleton className="h-8 w-16" />
                              <Skeleton className="h-8 w-16" />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  )}
                  {!checklistLoading &&
                    checklistItems.map((item, index) => (
                      <TableRow key={item.id}>
                        <TableCell className="text-center font-medium">{index + 1}</TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="font-medium">{item.name}</div>
                            {item.description && (
                              <div className="text-sm text-muted-foreground">{item.description}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-pre-wrap text-sm text-muted-foreground">
                          {item.instructions || '—'}
                        </TableCell>
                        <TableCell className="space-x-2 text-right">
                          <Button variant="outline" size="sm" onClick={() => handleOpenItemDialog(item)}>
                            {texts.actions?.edit ?? 'Редактировать'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600"
                            onClick={() => handleDeleteItem(item)}
                          >
                            {texts.actions?.delete ?? 'Удалить'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  {!checklistLoading && checklistItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground">
                        Данных пока нет
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <CardTitle>Расчет оценки звонка</CardTitle>
            <CardDescription>Получите оценку качества по идентификатору звонка</CardDescription>
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="UUID звонка"
              value={callId}
              onChange={(event) => setCallId(event.target.value)}
            />
            <Button onClick={handleLoadEvaluation} disabled={isLoading}>
              {isLoading ? 'Загрузка...' : 'Показать'}
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Dialog open={itemDialogOpen} onOpenChange={handleChecklistDialogChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Изменение критерия' : 'Новый критерий'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="rule-name">
                Название
              </label>
              <Input
                id="rule-name"
                value={itemForm.name}
                onChange={(event) => setItemForm((prev) => ({ ...prev, name: event.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="rule-description">
                Описание
              </label>
              <Input
                id="rule-description"
                value={itemForm.description}
                onChange={(event) =>
                  setItemForm((prev) => ({ ...prev, description: event.target.value }))
                }
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="rule-instructions">
                Инструкции для оценки
              </label>
              <Textarea
                id="rule-instructions"
                rows={4}
                value={itemForm.instructions}
                onChange={(event) =>
                  setItemForm((prev) => ({ ...prev, instructions: event.target.value }))
                }
              />
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => handleChecklistDialogChange(false)}>
              {texts.actions?.cancel ?? 'Отмена'}
            </Button>
            <Button onClick={handleSaveItem} disabled={itemForm.name.trim().length === 0}>
              {texts.actions?.save ?? 'Сохранить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {evaluation && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Сводка оценки</CardTitle>
              <CardDescription>Суммарный балл и процент выполнения чек-листа</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div>
                <div className="text-sm text-muted-foreground mb-1">Абсолютный балл</div>
                <div className="text-3xl font-bold">{totalScore.toFixed(1)}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Процент выполнения</div>
                <div className="flex items-center gap-3">
                  <Progress value={percentage} className="flex-1" />
                  <span className="text-xl font-semibold">{percentage.toFixed(1)}%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Детализация критериев</CardTitle>
              <CardDescription>Каждый критерий чек-листа и его фактический балл</CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Критерий</TableHead>
                    <TableHead>Категория</TableHead>
                    <TableHead>Вес</TableHead>
                    <TableHead>Баллы</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead>Комментарий</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(evaluation.evaluations ?? []).map((item) => (
                    <TableRow key={item.ruleId ?? item.ruleName}>
                      <TableCell>{item.ruleName}</TableCell>
                      <TableCell>{item.category || '—'}</TableCell>
                      <TableCell>{item.weight ?? '—'}</TableCell>
                      <TableCell>{item.score != null ? item.score.toFixed(1) : '—'}</TableCell>
                      <TableCell>
                        <Badge variant={item.passed ? 'secondary' : 'outline'}>
                          {item.passed ? 'Выполнено' : 'Не выполнено'}
                        </Badge>
                      </TableCell>
                      <TableCell>{item.comment || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {!evaluation && !isLoading && !error && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Введите идентификатор звонка, чтобы увидеть расшифровку оценки качества.
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EmployeeEvaluation;
